import { AxiosRequestConfig } from 'axios';
import {
  GetFileForDownloadParams,
  FilterDataParams,
  GetExcelCompanyProps,
  GetLeaseEndExcelProps,
  SetKilometersProps,
  UploadFilesProps,
  SetInProgressProps,
  CreateOrUpdateTireRequestProps,
  PlateHistoryPayload,
  CheckPlateNumberDuplicatePayload,
  CheckVinDuplicatePayload
} from './fleet.interface';
import { getRequestItem } from 'utils/get-response-data';
import { CrmDataTypes } from 'common/enums';
import { AnyObject, CrmResponse } from 'common/interfaces';

class FleetRequestConfig {
  getExcelCompany = ({
    ContactId,
    Pagination = { number: 100, start: 1 },
    Sort = {},
    Search = {},
    FieldsOrderType = 0,
    curDate = '',
    ProductActivated = true,
    columnsOrder
  }: GetExcelCompanyProps): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/getexcelcompanylist',
    data: {
      ContactId,
      Pagination,
      Sort,
      Search,
      FieldsOrderType,
      curDate,
      ProductActivated,
      columnsOrder
    }
  });

  getFilterData = (params: FilterDataParams): AxiosRequestConfig => ({
    method: 'post',
    url: 'fuhrpark/getFuhrparkListFilterData',
    data: params
  });

  getLeaseEndExcel = (data: GetLeaseEndExcelProps): AxiosRequestConfig => ({
    method: 'post',
    url: 'fuhrpark/getLeaseEndExcel',
    responseType: 'blob',
    data: data
  });

  unusedFleet = (ContactId: string): AxiosRequestConfig => ({
    method: 'post',
    url: 'fuhrpark/unusedfuhrapks',
    data: { ContactId }
  });

  descriptionChoices: AxiosRequestConfig = {
    method: 'post',
    url: 'fuhrpark/descriptionchoices',
    data: {}
  };

  setKilometers = (data: SetKilometersProps): AxiosRequestConfig => ({
    method: 'post',
    url: 'fuhrpark/CreateKmStand',
    data: data
  });

  getVehicleCosts = (fuhrparkId: string): AxiosRequestConfig => ({
    method: 'post',
    url: 'fuhrpark/GetFahrzeugkosten',
    data: { fuhrparkId }
  });

  getCalendarAppointment = (vehicleId: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetTerminkalender',
    data: { fuhrparkId: vehicleId }
  });

  getLeasingPeriod = (vehicleId: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetLeasingdauer',
    data: { fuhrparkId: vehicleId }
  });

  getById = (id?: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetFuhrparkById',
    data: { id }
  });

  updateFleet = (fleetId: string, attributes: Record<string, unknown>): AxiosRequestConfig => ({
    method: 'PUT',
    url: 'fuhrpark',
    data: {
      fuhrpark: {
        entityName: 'new_fuhrpark',
        id: fleetId,
        attributes
      }
    }
  });

  filesById = (vehicleId?: string): AxiosRequestConfig => ({
    method: 'GET',
    url: `fuhrpark/AllFiles?FuhrparkId=${vehicleId}`
  });

  getPoolCarClasses = (fuhrparkId: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetPoolcarClasses',
    data: { fuhrparkId }
  });

  getDamageHistory = (fuhrparkId: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetSchadenHistory',
    data: { fuhrparkId }
  });

  getEmailList = (fuhrparkid: string): AxiosRequestConfig => ({
    method: 'GET',
    url: 'fuhrpark/getemailslist',
    params: { fuhrparkid }
  });

  getDataQuality = (fuhrparkid: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetDatenqualitat',
    data: { fuhrparkid }
  });

  updateMainCar = (fuhrparkId: string, isMainCar: boolean): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/updateHauptauto',
    data: { fuhrparkId, isMainCar }
  });
  setInProgress = (data: SetInProgressProps) => ({
    method: 'POST',
    url: 'fuhrpark/SetInBetrieb',
    data
  });

  getAllCompanyVehicles = () => ({
    method: 'POST',
    url: 'fuhrpark/getFuhrparkByCompanyList',
    data: {}
  });

  createOrUpdateTire = ({ tireId, formData, uds_fuhrparkid }: CreateOrUpdateTireRequestProps): AxiosRequestConfig => {
    const data: AnyObject = {
      Reifen: {
        Attributes: {
          ...formData,
          uds_fuhrparkid
        }
      }
    };

    if (tireId) {
      data.Reifen.Id = tireId;
    }

    return {
      method: 'POST',
      url: 'fuhrpark/upsertreifen',
      data
    };
  };

  deactivateTire = (id: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/deactivatereifen',
    data: { id }
  });

  getTireManufacturers = (name: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetTireManufacturers',
    data: { name }
  });

  tireDealersQuery = (): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/getTireDealers',
    data: {}
  });

  checkActiveFuelCardExist = (id: string): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/checkActiveFuelCard',
    data: {
      fuhrparkId: id
    }
  });

  checkPlateNumberDuplicate = (data: CheckPlateNumberDuplicatePayload): AxiosRequestConfig => ({
    method: 'POST',
    url: 'Fuhrpark/CheckPlateNumberDuplicate',
    data
  });

  checkVinDuplicate = (data: CheckVinDuplicatePayload): AxiosRequestConfig => ({
    method: 'POST',
    url: 'Fuhrpark/CheckVinDuplicate',
    data
  });

  getPlateHistory = ({
    Pagination = { number: 100, start: 1 },
    Sort = {},
    Search = {},
    curDate = '',
    ProductActivated = true
  }: PlateHistoryPayload): AxiosRequestConfig => ({
    method: 'POST',
    url: 'fuhrpark/GetExcelKennzeichenHistory',
    data: {
      Pagination,
      Sort,
      Search,
      curDate,
      ProductActivated
    }
  });
}

export const fleetRequestConfig = new FleetRequestConfig();
